// =============================================================================
// Contrato já orçado — renovação e limites de alerta, na tela Lançar (062)
//
// Pedido do Financeiro em 15/10: informar a renovação dos contratos e mudar os
// limites de alerta "na tela de cadastro do orçamento". O formulário de cima
// cria linha NOVA; o orçamento de 2026 já está carregado, e cada contrato dele
// é um centro de custo + categoria (com as subcategorias), doze meses de linhas
// soltas. Este painel age sobre o que existe, sem criar orçamento:
//
//   · renovação manual por contrato (050), com o mesmo modal da Planilha e da
//     Central — a regra mora na RPC, não aqui;
//   · os dois limites de alerta (062), aplicados a todos os meses do exercício
//     ou de um mês em diante, numa ação só.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type FalhaApi } from "../lib/api.ts";
import { CabecalhoGrafico, Painel, Vazio } from "../componentes/ui.tsx";
import { ModalRenovacao, dataRenovacao, type AlvoRenovacao } from "./ModalRenovacao.tsx";

type Cadastros = {
  centros: { id: string; codigo: string; nome: string }[];
  categorias: { id: string; codigo: string | null; nome: string; natureza: string }[];
  exercicios: { ano: number; status: string }[];
};
type LinhaPlanilha = {
  centro_custo_id: string;
  categoria_id: string;
  competencia: string;
  subcategorias: { nome: string }[];
};
type Renovacao = {
  centro_custo_id: string; categoria_id: string; subcategoria: string | null;
  proxima_renovacao: string; observacao: string | null;
};
type Par = { pct_alerta_fixo: string; pct_alerta_variavel: string; linhas: number };

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto",
  "Setembro", "Outubro", "Novembro", "Dezembro"];
const seletor = "w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]";

/** Nome comparado como o banco compara (fn_normalizar_nome): sem acento, sem caixa. */
const normalizar = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

/** "30.00" → "30"; "12.50" → "12,5". */
const pctTela = (v: string) => String(Number(v)).replace(".", ",");

export function ContratoOrcado({ cadastros, anoInicial }: { cadastros: Cadastros; anoInicial: number }) {
  const qc = useQueryClient();
  const abertos = cadastros.exercicios.filter((e) => e.status !== "encerrado").map((e) => e.ano);
  const [ano, setAno] = useState(() => (abertos.includes(anoInicial) ? anoInicial : abertos[0] ?? anoInicial));
  const [cc, setCc] = useState("");
  const [cat, setCat] = useState("");
  const [alvo, setAlvo] = useState<AlvoRenovacao | null>(null);

  const categorias = cadastros.categorias.filter((c) => c.natureza === "despesa" || c.natureza === "investimento");
  const escolhido = !!cc && !!cat;

  // A mesma consulta (e o mesmo cache) da Planilha orçamentária: o modal de
  // renovação já a invalida ao salvar.
  const planilha = useQuery({
    queryKey: ["planilha", ano],
    queryFn: () => api.get<{ linhas: LinhaPlanilha[]; renovacoes?: Renovacao[] }>(`/api/planilha?ano=${ano}`),
    enabled: escolhido,
  });
  const limiares = useQuery({
    queryKey: ["limiares", ano, cc, cat],
    queryFn: () => api.get<{ pares: Par[] }>(
      `/api/limiares?ano=${ano}&centro_custo_id=${cc}&categoria_id=${cat}`),
    enabled: escolhido,
  });

  const linhas = useMemo(
    () => (planilha.data?.linhas ?? []).filter((l) => l.centro_custo_id === cc && l.categoria_id === cat),
    [planilha.data, cc, cat],
  );
  const subcategorias = useMemo(() => {
    const vistos = new Map<string, string>();
    for (const l of linhas) for (const s of l.subcategorias ?? []) {
      if (!vistos.has(normalizar(s.nome))) vistos.set(normalizar(s.nome), s.nome);
    }
    return [...vistos.values()].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [linhas]);
  const renovacoes = useMemo(
    () => (planilha.data?.renovacoes ?? []).filter((r) => r.centro_custo_id === cc && r.categoria_id === cat),
    [planilha.data, cc, cat],
  );
  const renovacaoDe = (sub: string | null) =>
    renovacoes.find((r) => (r.subcategoria === null ? sub === null : sub !== null && normalizar(r.subcategoria) === normalizar(sub)));

  // Como a Planilha: a renovação vai na subcategoria; na linha inteira só
  // quando não há subcategoria, ou quando já foi informada ali.
  const contratos: (string | null)[] = subcategorias.length === 0 || renovacaoDe(null)
    ? [null, ...subcategorias] : subcategorias;

  const nomeCc = cadastros.centros.find((c) => c.id === cc);
  const nomeCat = categorias.find((c) => c.id === cat);
  const rotuloBase = nomeCc && nomeCat ? `${nomeCc.codigo} ${nomeCc.nome} · ${nomeCat.nome}` : "";

  return (
    <Painel className="mt-4">
      <CabecalhoGrafico
        titulo="Contrato já orçado: renovação e alertas"
        dica="Age sobre o orçamento que já existe, sem criar linha nova. Para orçar, use o formulário acima."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="text-[12px] font-semibold text-tinta-media">Exercício</span>
          <select value={ano} onChange={(e) => setAno(Number(e.target.value))} className={`mt-1 ${seletor}`}>
            {abertos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[12px] font-semibold text-tinta-media">Centro de custo</span>
          <select value={cc} onChange={(e) => setCc(e.target.value)} className={`mt-1 ${seletor}`}>
            <option value="">Selecione…</option>
            {cadastros.centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[12px] font-semibold text-tinta-media">Categoria financeira</span>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={`mt-1 ${seletor}`}>
            <option value="">Selecione…</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ""}{c.nome}</option>
            ))}
          </select>
        </label>
      </div>

      {!escolhido && (
        <p className="mt-3 text-[12.5px] text-tinta-fraca">
          Escolha o centro de custo e a categoria do contrato.
        </p>
      )}

      {escolhido && (planilha.isPending || limiares.isPending) && (
        <p className="mt-3 text-[13px] text-tinta-fraca">Carregando…</p>
      )}

      {escolhido && (planilha.isError || limiares.isError) && (
        <div className="mt-3">
          <Vazio
            titulo="Não foi possível carregar o orçamento deste contrato"
            dica={((planilha.error ?? limiares.error) as FalhaApi | null)?.message}
          />
        </div>
      )}

      {escolhido && planilha.data && limiares.data && linhas.length === 0 && (
        <div className="mt-3">
          <Vazio
            titulo={`Não há orçamento para esta combinação em ${ano}`}
            dica="Lance a linha no formulário acima primeiro; a renovação e os alertas valem para o que está orçado."
          />
        </div>
      )}

      {escolhido && planilha.data && limiares.data && linhas.length > 0 && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {/* ── renovação ─────────────────────────────────────────────── */}
          <div className="rounded-lg border border-linha p-3">
            <span className="text-[12px] font-semibold text-tinta-media">Renovação manual</span>
            <span className="mt-0.5 block text-[11.5px] text-tinta-fraca">
              Só para contrato que não renova sozinho. O sistema avisa aos 60, aos 30 e a 2 dias da data.
            </span>
            <ul className="mt-2">
              {contratos.map((sub) => {
                const reg = renovacaoDe(sub);
                return (
                  <li key={sub ?? "(linha)"} className="flex items-center justify-between gap-3 border-t border-linha py-2 first:border-t-0">
                    <span className="min-w-0">
                      <span className="block truncate text-[13.5px]" title={sub ?? undefined}>
                        {sub ?? "A linha inteira"}
                      </span>
                      <span className="block text-[11.5px] text-tinta-fraca">
                        {reg ? `renova ${dataRenovacao(reg.proxima_renovacao)}${reg.observacao ? ` · ${reg.observacao}` : ""}` : "sem renovação manual"}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setAlvo({
                        centro_custo_id: cc, categoria_id: cat, subcategoria: sub,
                        rotulo: sub ? `${rotuloBase} › ${sub}` : rotuloBase,
                        proxima_renovacao: reg?.proxima_renovacao ?? null,
                        observacao: reg?.observacao ?? null,
                        registrado: !!reg,
                      })}
                      className="shrink-0 rounded-lg border border-linha px-3 py-1.5 text-[12.5px] hover:bg-superficie-2"
                    >
                      {reg ? "Alterar" : "Informar"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ── limites de alerta ─────────────────────────────────────── */}
          <Limites
            key={`${ano}:${cc}:${cat}`}
            ano={ano} cc={cc} cat={cat} pares={limiares.data.pares}
            onSalvo={() => void qc.invalidateQueries({
              predicate: (x) => ["limiares", "alertas", "visao-financeiro", "painel-gestor", "excecoes", "contadores", "avisos"]
                .includes(String(x.queryKey[0])),
            })}
          />
        </div>
      )}

      {alvo && <ModalRenovacao alvo={alvo} onFechar={() => setAlvo(null)} />}
    </Painel>
  );
}

function Limites({ ano, cc, cat, pares, onSalvo }: {
  ano: number; cc: string; cat: string; pares: Par[]; onSalvo: () => void;
}) {
  // O par mais comum preenche os campos; com mais de um, a tela diz que os
  // meses estão diferentes antes de alguém sobrescrever sem saber.
  const principal = pares[0];
  const [variavel, setVariavel] = useState(principal ? pctTela(principal.pct_alerta_variavel) : "80");
  const [fixo, setFixo] = useState(principal ? pctTela(principal.pct_alerta_fixo) : "30");
  const [desde, setDesde] = useState("");

  const salvar = useMutation({
    mutationFn: () => api.post<{ mudou: boolean; linhas: number }>("/api/limiares", {
      centro_custo_id: cc,
      categoria_id: cat,
      exercicio_ano: ano,
      a_partir_de: desde || null,
      pct_alerta_variavel: variavel.replace(",", "."),
      pct_alerta_fixo: fixo.replace(",", "."),
    }),
    onSuccess: onSalvo,
  });

  const numero = (v: string) => Number(v.replace(",", "."));
  const valido = numero(variavel) >= 1 && numero(variavel) <= 99 && numero(fixo) >= 1 && numero(fixo) <= 999;

  return (
    <div className="rounded-lg border border-linha p-3">
      <span className="text-[12px] font-semibold text-tinta-media">Alertas</span>
      <span className="mt-0.5 block text-[11.5px] text-tinta-fraca">
        O padrão é 80% e 30%. Vale para os meses do exercício, de uma vez.
      </span>

      {pares.length > 1 && (
        <p className="mt-2 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12px] text-atencao-tinta">
          Os meses deste contrato têm limites diferentes:{" "}
          {pares.map((p) => `${pctTela(p.pct_alerta_variavel)}% e ${pctTela(p.pct_alerta_fixo)}% em ${p.linhas} ${p.linhas === 1 ? "mês" : "meses"}`).join("; ")}.
        </p>
      )}

      <div className="mt-2 grid gap-3 sm:grid-cols-2 sm:items-end">
        <label className="block">
          <span className="text-[12px] font-semibold text-tinta-media">Avisar quando o gasto do mês chegar a</span>
          <span className="mt-1 flex items-center gap-2">
            <input type="text" inputMode="decimal" value={variavel} onChange={(e) => setVariavel(e.target.value)}
              className="num w-20 rounded-lg border border-linha bg-superficie px-3 py-2 text-right text-[14px]" />
            <span className="text-[13px] text-tinta-fraca">% do orçado</span>
          </span>
        </label>
        <label className="block">
          <span className="text-[12px] font-semibold text-tinta-media">Na despesa recorrente, avisar quando o mês passar</span>
          <span className="mt-1 flex items-center gap-2">
            <input type="text" inputMode="decimal" value={fixo} onChange={(e) => setFixo(e.target.value)}
              className="num w-20 rounded-lg border border-linha bg-superficie px-3 py-2 text-right text-[14px]" />
            <span className="text-[13px] text-tinta-fraca">% acima do orçado</span>
          </span>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[12px] font-semibold text-tinta-media">Aplicar a</span>
          <select value={desde} onChange={(e) => setDesde(e.target.value)} className={`mt-1 ${seletor}`}>
            <option value="">Todos os meses de {ano}</option>
            {MESES.map((m, i) => (
              <option key={m} value={`${ano}-${String(i + 1).padStart(2, "0")}-01`}>De {m.toLowerCase()} em diante</option>
            ))}
          </select>
        </label>
      </div>

      {!valido && (
        <p className="mt-2 text-[12px] text-risco-tinta">
          O aviso de consumo vai de 1% a 99% (em 100% já é o estouro); o de aumento, de 1% a 999%.
        </p>
      )}
      {salvar.isError && (
        <p className="mt-2 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(salvar.error as FalhaApi).message}
        </p>
      )}
      {salvar.isSuccess && (
        <p className="mt-2 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[12.5px] text-ok-tinta">
          {salvar.data.mudou
            ? `Limites aplicados a ${salvar.data.linhas} ${salvar.data.linhas === 1 ? "mês" : "meses"}. Os alertas dos meses que já passaram foram refeitos.`
            : "Nada mudou: os meses já estavam com esses limites."}
        </p>
      )}

      <div className="mt-3 flex justify-end">
        <button type="button" disabled={!valido || salvar.isPending} onClick={() => salvar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {salvar.isPending ? "Salvando…" : "Aplicar limites"}
        </button>
      </div>
    </div>
  );
}
